<template>
  <Transition
    @before-enter="beforeEnter"
    @enter="enter"
    @after-enter="reset"
    @enter-cancelled="cancel"
    @leave="leave"
    @after-leave="reset"
    @leave-cancelled="cancel"
  >
    <slot />
  </Transition>
</template>

<script lang="ts" setup>
import * as animate from '@/util/animate'

const MINIMUM_DURATION_FRACTION = 0.9

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    duration?: number
  }>(),
  {
    disabled: false,
    duration: 500,
  },
)

function cancel(element: Element) {
  if (!(element instanceof HTMLElement)) return

  Object.assign(element.style, {
    height: '',
  })
}

function beforeEnter(element: Element) {
  if (props.disabled) return
  if (!(element instanceof HTMLElement)) return

  element.style.opacity = '0'
}

function enter(element: Element, done: () => void) {
  if (props.disabled || !(element instanceof HTMLElement)) {
    done()
    return
  }

  // Clamp the animated content to avoid animation outside the viewport
  const rect = element.getBoundingClientRect()
  const availableHeight = window.innerHeight - rect.top

  // Element is outside viewport, don't animate
  if (availableHeight < 0 || rect.bottom < 0) {
    done()
    return
  }

  const effectiveHeight = Math.min(availableHeight, rect.height)
  const effectiveHeightFraction = effectiveHeight / window.innerHeight

  const duration = Math.max(
    MINIMUM_DURATION_FRACTION * props.duration,
    effectiveHeightFraction * props.duration,
  )
  let effectiveDuration = duration
  if (effectiveHeight < rect.height) {
    const hiddenHeight = rect.height - effectiveHeight
    const hiddenHeightFraction = hiddenHeight / effectiveHeight
    effectiveDuration *= 1 + hiddenHeightFraction
  }

  Object.assign(element.style, {
    overflow: 'hidden',
  })

  if (effectiveDuration > duration) {
    Object.assign(element.style, {
      opacity: '',
      height: `${effectiveHeight}px`,
    })

    animate.from(
      element,
      {
        opacity: 0,
        height: 0,
        marginTop: 0,
        marginBottom: 0,
        paddingTop: 0,
        paddingBottom: 0,
      },
      {
        duration: duration,
        onComplete() {
          done()
        },
      },
    )
  } else {
    Object.assign(element.style, {
      opacity: '',
    })

    animate.from(
      element,
      {
        opacity: 0,
        height: 0,
        marginTop: 0,
        marginBottom: 0,
        paddingTop: 0,
        paddingBottom: 0,
      },
      {
        duration,
        onComplete() {
          done()
        },
      },
    )
  }
}

function leave(element: Element, done: () => void) {
  if (props.disabled || !(element instanceof HTMLElement)) {
    done()
    return
  }

  // Clamp the animated content to avoid animation outside the viewport
  const rect = element.getBoundingClientRect()
  const availableHeight = window.innerHeight - rect.top
  const effectiveHeight = Math.min(availableHeight, rect.height)
  const effectiveHeightFraction = effectiveHeight / window.innerHeight

  const duration = Math.max(
    MINIMUM_DURATION_FRACTION * props.duration,
    effectiveHeightFraction * props.duration,
  )

  Object.assign(element.style, {
    overflow: 'hidden',
  })

  if (effectiveHeight < rect.height) {
    Object.assign(element.style, {
      height: `${effectiveHeight}px`,
    })
  }

  animate.to(
    element,
    {
      height: 0,
      opacity: 0,
      marginTop: 0,
      marginBottom: 0,
      paddingTop: 0,
      paddingBottom: 0,
    },
    {
      duration,
      onComplete() {
        done()
      },
    },
  )
}

function reset(element: Element) {
  if (props.disabled) return
  if (!(element instanceof HTMLElement)) return

  Object.assign(element.style, {
    height: '',
    opacity: '',
    marginTop: '',
    marginBottom: '',
    paddingTop: '',
    paddingBottom: '',
    overflow: '',
  })
}
</script>
